import type { Client } from '@urql/core'
import { computed, makeObservable, runInAction } from 'mobx'
import { bg } from 'lib/background'
import { categoryResolver, type CategoryResolver } from 'lib/cdtCodeDefaults'
import { mergeRecentWrites, type RecentWrite } from 'lib/recentWrites'
import { graphql } from 'app/generated/gql'

import type { CdtCodeMapping as CdtCodeMappingType, CdtCodeMappingListResult } from 'app/generated/graphql'
import { EntityStore } from './EntityStore'

const LIST_QUERY = graphql(`
  query CdtCodeMappings($search: String, $pagination: PaginationInput, $sort: SortInput) {
    cdtCodeMappings(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        category
        cdtCode
        createdDate
        id
        orgId
        updatedDate
        version
        workspaceId
      }
      total
      page
    }
  }
`)

const CREATE_MUTATION = graphql(`
  mutation CreateCdtCodeMappingStore($input: CreateCdtCodeMappingInput!) {
    createCdtCodeMapping(input: $input) {
      category
      cdtCode
      createdDate
      id
      orgId
      updatedDate
      version
      workspaceId
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateCdtCodeMappingStore($id: ID!, $input: UpdateCdtCodeMappingInput!) {
    updateCdtCodeMapping(id: $id, input: $input) {
      category
      cdtCode
      createdDate
      id
      orgId
      updatedDate
      version
      workspaceId
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteCdtCodeMapping($id: ID!) {
    deleteCdtCodeMapping(id: $id)
  }
`)

const CHANNEL_NAME = `trackable:cdt-code-mappings`

const RECONCILE_DELAY_MS = 2_000

type MappingRow = Pick<CdtCodeMappingType, `id`> & Partial<CdtCodeMappingType>

export type CdtMappingChange = {
  workspaceId: string
  op: `create` | `update` | `remove`
  mapping: { id: string; cdtCode?: string; category?: string; updatedDate?: string }
}

export class CdtCodeMappingStore extends EntityStore<
  CdtCodeMappingType,
  CdtCodeMappingListResult,
  typeof CREATE_MUTATION,
  typeof UPDATE_MUTATION
> {
  private loadedWorkspaceId: string | undefined
  private requestedWorkspaceId: string | undefined
  private inflight: Promise<void> | undefined
  private channel: BroadcastChannel | undefined
  private reconcileTimer: ReturnType<typeof setTimeout> | undefined

  /**
   * The cdtCodeMappings query reads from a search index that lags writes by up to a second,
   * so a refetch right after a write returns the old rows. Writes from this tab and from
   * other tabs are kept for ten seconds and merged over every fetched list.
   */
  private recentWrites = new Map<string, RecentWrite<MappingRow>>()

  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: undefined,
      createMutation: CREATE_MUTATION,
      updateMutation: UPDATE_MUTATION,
      deleteMutation: DELETE_MUTATION,
      typename: `CdtCodeMapping`,
      extractList: (data: any) => data.cdtCodeMappings,
      extractMutated: (data: any) => data.updateCdtCodeMapping ?? data.createCdtCodeMapping,
    })
    makeObservable(this, { categoryOf: computed({ keepAlive: true }) })
    if (typeof BroadcastChannel !== `undefined`) {
      this.channel = new BroadcastChannel(CHANNEL_NAME)
      this.channel.onmessage = (event) => this.receive(event.data)
    }
    if (typeof document !== `undefined`) {
      document.addEventListener(`visibilitychange`, () => {
        if (document.visibilityState === `visible`) this.refresh()
      })
    }
  }

  get categoryOf(): CategoryResolver {
    return categoryResolver(this.hits)
  }

  ensureLoaded(workspaceId: string): Promise<void> {
    if (this.loadedWorkspaceId === workspaceId) return this.inflight ?? Promise.resolve()
    this.reset()
    return this.load(workspaceId, `cache-first`)
  }

  announceChange(change: CdtMappingChange): void {
    this.channel?.postMessage(change)
  }

  async create(
    input: Parameters<EntityStore<CdtCodeMappingType, CdtCodeMappingListResult, typeof CREATE_MUTATION>[`create`]>[0],
  ) {
    const result = await super.create(input)
    const created = !result.error && result.data ? this.config.extractMutated?.(result.data) : undefined
    if (created?.id) this.recentWrites.set(created.id, { row: created, at: Date.now() })
    return result
  }

  async update(
    id: string,
    input: Parameters<
      EntityStore<CdtCodeMappingType, CdtCodeMappingListResult, undefined, typeof UPDATE_MUTATION>[`update`]
    >[1],
  ) {
    const result = await super.update(id, input)
    const updated = !result.error && result.data ? this.config.extractMutated?.(result.data) : undefined
    if (updated?.id) this.recentWrites.set(id, { row: updated, at: Date.now() })
    return result
  }

  async remove(id: string) {
    const result = await super.remove(id)
    if (!result.error) this.recentWrites.set(id, { row: null, at: Date.now() })
    return result
  }

  reset() {
    super.reset()
    this.loadedWorkspaceId = undefined
    this.requestedWorkspaceId = undefined
    this.inflight = undefined
    this.recentWrites.clear()
    clearTimeout(this.reconcileTimer)
  }

  protected overlayList(): void {
    if (!this.list) return
    runInAction(() => {
      const hits = mergeRecentWrites(
        this.list!.hits,
        this.recentWrites,
        (row) => this.intern(row as CdtCodeMappingType)!,
      )
      if (hits) this.list = { ...this.list!, hits, total: hits.length }
    })
  }

  private receive(message: unknown): void {
    const change = message as Partial<CdtMappingChange> | undefined
    if (!change?.mapping?.id || !change.op || change.workspaceId !== this.requestedWorkspaceId) return
    const { id } = change.mapping
    this.recentWrites.set(id, { row: change.op === `remove` ? null : change.mapping, at: Date.now() })
    this.overlayList()
    clearTimeout(this.reconcileTimer)
    this.reconcileTimer = setTimeout(() => this.refresh(), RECONCILE_DELAY_MS)
  }

  private refresh(): void {
    if (this.requestedWorkspaceId) bg(this.load(this.requestedWorkspaceId, `network-only`))
  }

  private load(workspaceId: string, requestPolicy: `cache-first` | `network-only`): Promise<void> {
    this.loadedWorkspaceId = workspaceId
    this.requestedWorkspaceId = workspaceId
    const request = this.fetchList({ search: workspaceId }, { requestPolicy }).then((result) => {
      if (result.error) {
        if (!this.list && this.loadedWorkspaceId === workspaceId && this.inflight === request) {
          this.loadedWorkspaceId = undefined
        }
      }
      if (this.inflight === request) this.inflight = undefined
    })
    this.inflight = request
    return request
  }
}
